import type {
  CardSessionControls,
  SessionView,
} from '@littlebird/action-cards-view/react';

export type StatusLineOverlay = {
  text: string;
  tone: 'default' | 'muted' | 'danger';
  action?: { label: string; run: () => void };
  // Shown in place of `text` when `text` doesn't fit the line's width
  countText?: string;
};

type Verbs = { pendingVerb?: string; fixVerb: string };

export const stillRunningText = (pendingVerb: string) =>
  `Still ${pendingVerb.toLowerCase()}…`;

type ReserveProps = {
  text?: string;
  reserveText?: string;
  pendingVerb?: string;
  fixVerb?: string;
  // The time as the line shows it, joined to the text the way the line joins it
  time?: string;
  timeJoin?: string;
  quietWhileRunning?: boolean;
};

// Every copy the line can show on its surface for the card's status, so its width
// never changes as one replaces another. "Edited" only ever replaces a
// draft's text. A row is never edited or run from, so it keeps its own text and the
// reservation its document sends. A row reserves the fix count too, but only
// when the card can ever have one: a surface reserves only copy it can show.
// While a row shows the count, its own text is hidden, so it is not reserved
export function statusLineReserve(
  {
    text,
    reserveText,
    pendingVerb,
    fixVerb = 'send',
    time,
    timeJoin,
    quietWhileRunning = false,
  }: ReserveProps,
  status: string | undefined,
  row: boolean,
  hasChecks = false,
  fixing = false
): string[] {
  const shown = [text, time].filter(Boolean).join(timeJoin ?? ' · ');
  // A dismissed or withdrawn row changes only by leaving that state, so it
  // reserves nothing beyond what it shows
  if (row && (status === 'dismissed' || status === 'revoked')) return [shown];
  // A line says a slow run is still going only in the card's own verb, and a row
  // only while it runs
  const still =
    pendingVerb && !quietWhileRunning ? [stillRunningText(pendingVerb)] : [];
  const rowStill = status === 'running' ? still : [];
  if (row && fixing) return [reserveText ?? '', 'Fix 2 things', ...rowStill];
  if (row)
    return [
      reserveText ?? '',
      shown,
      ...(hasChecks ? ['Fix 2 things'] : []),
      ...rowStill,
    ];
  if (reserveText) return [reserveText, ...still];
  return [
    ...still,
    text ?? '',
    ...(status === 'draft' ? ['Edited'] : []),
    `Fix 99 things to ${fixVerb}`,
    'Not saved · Retry',
  ];
}

// What the card's session says in place of the status line's own text, most
// urgent first; undefined leaves the line to its text. A row never shows a
// field's own message, only the count, with no verb
export function statusLineOverlay(
  session: SessionView,
  controls: CardSessionControls,
  { pendingVerb, fixVerb }: Verbs,
  stillRunning = false,
  row = false
): StatusLineOverlay | undefined {
  const { armed, validationCount } = session;
  if (stillRunning && pendingVerb)
    return { text: stillRunningText(pendingVerb), tone: 'default' };
  if (armed?.hint) return { text: armed.hint, tone: 'default' };
  if (validationCount > 0) {
    const things = validationCount === 1 ? 'thing' : 'things';
    if (row)
      return { text: `Fix ${validationCount} ${things}`, tone: 'danger' };
    // One problem is named while it fits the line; otherwise, and for
    // several, it is counted. Each field marks itself and carries its full
    // message in its description
    const count = `Fix ${validationCount} ${things} to ${fixVerb}`;
    return validationCount === 1 && session.validationMessage
      ? { text: session.validationMessage, tone: 'danger', countText: count }
      : { text: count, tone: 'danger' };
  }
  if (session.saveFailed) {
    return {
      text: 'Not saved',
      tone: 'danger',
      action: { label: 'Retry', run: controls.retrySave },
    };
  }
  return undefined;
}
